// src/components/manager/ManagerQuoteReview.js
import React, { useState, useEffect, useMemo } from 'react';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import { calculateDeliveryCost } from '../common/deliveryCalculator';
import AddressBookModal from '../common/AddressBookModal';
import PrintlogicCustomerModal from './PrintlogicCustomerModal';
import PrintlogicOrderStatusModal from '../common/PrintlogicOrderStatusModal';

// TILL SALE customer for quick one-off quotes
const TILL_SALE_CUSTOMER = {
  customer_id: '3374911',
  customer_name: 'TILL SALE'
};

const ManagerQuoteReview = ({ onClose, preSelectedCustomer = null }) => {
  const {
    currentQuote,
    removeProductFromQuote,
    updateProductInQuote,
    addQuote,
    clearCurrentQuote,
    approveAndSubmitToPrintSystem,
    getEffectivePrice
  } = useQuotes();
  const { hasRole, isAdmin, currentUser } = useAuth();

  const [quoteName, setQuoteName] = useState('');
  const [clientName, setClientName] = useState('');
  const [customerName, setCustomerName] = useState(preSelectedCustomer?.customer_name || '');
  const [notes, setNotes] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [dateRequired, setDateRequired] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmittingToPrintlogic, setIsSubmittingToPrintlogic] = useState(false);
  const [isSubmissionSuccessful, setIsSubmissionSuccessful] = useState(false);
  const [showPrintlogicStatusModal, setShowPrintlogicStatusModal] = useState(false);

  // Override editing state
  const [overrideEditId, setOverrideEditId] = useState(null);
  const [overrideValue, setOverrideValue] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const enterSubmitRef = React.useRef(false);

  // Delivery-related state
  const [deliveryOption, setDeliveryOption] = useState('collection');
  const [deliveryAddressText, setDeliveryAddressText] = useState('');
  const [expressSubOption, setExpressSubOption] = useState(null);

  // Address book state
  const [showAddressBookModal, setShowAddressBookModal] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);

  // Multi-address delivery state
  const [numberOfAddresses, setNumberOfAddresses] = useState(2);
  const [manualAddresses, setManualAddresses] = useState('');

  // Manager-specific: Printlogic customer selection state
  const [showPrintlogicCustomerModal, setShowPrintlogicCustomerModal] = useState(false);
  const [selectedPrintlogicCustomer, setSelectedPrintlogicCustomer] = useState(preSelectedCustomer);

  // Rename functionality state
  const [renamingItemId, setRenamingItemId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const totalProductsPrice = useMemo(
    () => currentQuote.items.reduce((sum, item) => sum + getEffectivePrice(item), 0),
    [currentQuote.items, getEffectivePrice]
  );

  const multiPieceGroupMeta = useMemo(() => {
    const map = new Map();

    currentQuote.items.forEach((item, index) => {
      if (!item.multiPieceGroupId) {
        return;
      }

      const existing = map.get(item.multiPieceGroupId);
      const summary = item.multiPieceGroupSummary || existing?.summary || null;
      const effective = getEffectivePrice(item);

      map.set(item.multiPieceGroupId, {
        name: item.multiPieceGroupName || summary?.groupName || existing?.name || item.jobName,
        count:
          item.multiPieceGroupCount ||
          summary?.totalPieces ||
          existing?.count ||
          1,
        firstIndex: existing ? Math.min(existing.firstIndex, index) : index,
        summary,
        effectiveTotal: (existing?.effectiveTotal || 0) + effective,
      });
    });

    return map;
  }, [currentQuote.items, getEffectivePrice]);

  const formatPrice = (value) => `£${Number(value || 0).toFixed(2)}`;

  const canOverridePrices = hasRole('manager') || isAdmin();

  const startOverrideEdit = (product) => {
    setOverrideEditId(product.id);
    setOverrideValue(
      product.overridePrice !== undefined && product.overridePrice !== null
        ? String(product.overridePrice)
        : ''
    );
    setOverrideReason(product.overrideReason || '');
  };

  const cancelOverrideEdit = () => {
    setOverrideEditId(null);
    setOverrideValue('');
    setOverrideReason('');
  };

  const handleOverrideKeyDown = (e, product) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveOverride(product);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelOverrideEdit();
    }
  };

  const saveOverride = (product) => {
    const parsed = parseFloat(overrideValue);
    if (Number.isNaN(parsed)) {
      alert('Please enter a numeric override price.');
      return;
    }
    if (parsed < 0) {
      alert('Override price must be zero or higher.');
      return;
    }

    const calculatedBase =
      product.calculatedPrice ??
      product.priceBeforeOverride ??
      product.price ??
      0;
    if (calculatedBase > 0) {
      const minAllowed = calculatedBase * 0.85;
      if (parsed < minAllowed) {
        alert(
          `Override can't be more than 15% below calculated ${formatPrice(calculatedBase)}. Minimum allowed: ${formatPrice(minAllowed)}`
        );
        return;
      }

      const maxReasonable = calculatedBase * 3;
      if (parsed > maxReasonable) {
        const warnOptions = [
          "We print banners and booklets, not banknotes. Are you sure you wish to proceed?",
          "OverflowError: margin too large for human comprehension. Are you sure you wish to proceed?",
          "This price is riding a rocket. Confirm if you really want to launch.",
          "Caution: overriding into the stratosphere. Proceed anyway?",
          "Whoa there—are we quoting a moon landing? Confirm to continue.",
          "Did you fat finger that price?? Double-check before proceeding.",
          "Are you being serious? This price is sky-high! Confirm to proceed.",
          "This override price seems... ambitious. Are you sure you want to continue?"
        ];
        const warnMessage = warnOptions[Math.floor(Math.random() * warnOptions.length)];
        const proceed = window.confirm(`${warnMessage}\n\nCalculated: ${formatPrice(calculatedBase)}\nOverride: ${formatPrice(parsed)}`);
        if (!proceed) {
          return;
        }
      }
    }

    updateProductInQuote(product.id, {
      overridePrice: parsed,
      overrideReason: overrideReason || '',
      overriddenBy: currentUser?.username || currentUser?.email || 'manager',
      overriddenAt: new Date().toISOString(),
    });
    cancelOverrideEdit();
  };

  const clearOverride = (product) => {
    updateProductInQuote(product.id, {
      overridePrice: null,
      overrideReason: '',
      overriddenBy: null,
      overriddenAt: null,
    });
    cancelOverrideEdit();
  };

  // Calculate delivery costs
  const expressDeliveryDetails = calculateDeliveryCost(currentQuote.items, 'express', expressSubOption);
  const expressDeliveryCost = expressDeliveryDetails.cost;
  const hasOversizedItems = expressDeliveryDetails.hasOversizedItems;
  
  // Check if we have products to submit
  const hasProducts = currentQuote.items.length > 0;

  // Determine if the form should be displayed (items in quote OR submission was successful)
  const displayForm = currentQuote.items.length > 0 || isSubmissionSuccessful;

  // Calculate delivery costs
  let deliveryCost = 0;
  if (deliveryOption === 'express') {
    deliveryCost = expressDeliveryCost;
  } else if (deliveryOption === 'multi-address') {
    deliveryCost = numberOfAddresses * 9.5; // £9.50 per address
  }

  const finalTotalPrice = totalProductsPrice + deliveryCost;

  // Helper to check if TILL SALE customer is selected
  const isTillSaleSelected = selectedPrintlogicCustomer?.customer_id === '3374911';

  // Managers don't need delivery address validation - they have different workflow needs
  const isDeliveryAddressValid = () => {
    return true; // Managers can submit quotes without complete delivery addresses
  };

  // Handle address selection from address book
  const handleAddressSelected = (address) => {
    setSelectedAddress(address);
    
    // Format address as multi-line text
    const addressText = [
      address.address_line1,
      address.address_line2,
      address.city,
      address.postcode,
      address.delivery_instructions
    ].filter(Boolean).join('\n');
    
    setDeliveryAddressText(addressText);
    
    // Update client name and company name fields
    setClientName(address.customer_name);
    setCompanyName(address.company_name || '');
  };

  // Handle adding new address
  const handleAddNewAddress = () => {
    // For now, just close the modal and let user manually enter
    setShowAddressBookModal(false);
  };

  // Manager-specific: Handle Printlogic customer selection
  const handlePrintlogicCustomerSelected = (customer) => {
    setSelectedPrintlogicCustomer(customer);

    // Set customerName to the Printlogic account name (system identifier)
    setCustomerName(customer.customer_name);

    // Auto-fill the client name with the selected customer's name (user can edit)
    setClientName(customer.customer_name);

    // Clear company name as it will be handled by Printlogic customer data
    setCompanyName('');
  };

  // Manager-specific: Clear selected Printlogic customer
  const clearPrintlogicCustomer = () => {
    setSelectedPrintlogicCustomer(null);
    // Note: We don't automatically clear clientName as user might want to keep it
  };

  // Manager-specific: Quick-select TILL SALE customer
  const handleQuickSelectTillSale = () => {
    handlePrintlogicCustomerSelected(TILL_SALE_CUSTOMER);
  };

  // Handle item rename
  const handleRenameStart = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    setRenamingItemId(product.id);
    setRenameValue(product.jobName);
  };

  const handleRenameCancel = () => {
    setRenamingItemId(null);
    setRenameValue('');
  };

  const handleRenameSave = () => {
    if (renameValue.trim() !== '') {
      updateProductInQuote(renamingItemId, { jobName: renameValue.trim() });
    }
    setRenamingItemId(null);
    setRenameValue('');
  };

  const handleRenameKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleRenameSave();
    } else if (e.key === 'Escape') {
      handleRenameCancel();
    }
  };

  // Manager-specific: Handle approve and submit to Printlogic
  const submitOrderToPrintlogic = async (orderStatus) => {
    if (!quoteName.trim() || !clientName.trim()) {
      alert('Please enter both quote name and client name');
      return;
    }

    setIsSubmittingToPrintlogic(true);
    const itemsToSubmit = [...currentQuote.items];

    // Create the delivery info object
    const deliveryInfo = {
      option: deliveryOption,
      cost: deliveryCost,
      boxesNeeded: deliveryOption === 'express' ? expressDeliveryDetails.boxesNeeded : 0,
      estimatedWeight: deliveryOption === 'express' ? expressDeliveryDetails.estimatedWeight : 0,
      hasOversizedItems: hasOversizedItems,
      address: deliveryOption === 'express' ? deliveryAddressText : null,
      numberOfAddresses: deliveryOption === 'multi-address' ? numberOfAddresses : null,
      addresses: deliveryOption === 'multi-address' ? manualAddresses : null,
      expressSubOption: deliveryOption === 'express' ? expressSubOption : null,
      expressSubOptionCost: deliveryOption === 'express' ? expressDeliveryDetails.expressSubOptionCost : 0,
    };

    // Create the quote object
    const quoteData = {
      quoteName,
      clientName,
      companyName,
      customerName,
      dateRequired,
      notes,
      items: itemsToSubmit,
      totalProductsPrice,
      deliveryInfo,
      totalPrice: finalTotalPrice,
      // Preserve convertedFromDraft flag if it exists on currentQuote
      ...(currentQuote.convertedFromDraft && { convertedFromDraft: true })
    };

    // Add Printlogic customer data if selected
    if (selectedPrintlogicCustomer) {
      quoteData.printlogic_customer_id = selectedPrintlogicCustomer.customer_id;
      quoteData.printlogic_account_name = selectedPrintlogicCustomer.customer_name;
    }

    try {
      // Step 1: Submit the quote
      const result = await addQuote(quoteData);
      
      if (result.success) {
        // Step 2: Approve and submit to Printlogic
        const printlogicResult = await approveAndSubmitToPrintSystem(result.quote.id, notes, orderStatus);

        if (printlogicResult.success) {
          setIsSubmissionSuccessful(true);
          setSuccessMessage(`Order created and sent to Printlogic successfully! Order #${printlogicResult.orderId}`);
          setTimeout(() => {
            onClose({
              type: 'order_submitted_to_printlogic',
              orderNumber: printlogicResult.orderId,
              quoteName: quoteName
            });
          }, 2500);
        } else {
          setSuccessMessage('⚠ Quote saved but failed to send to Printlogic: ' + printlogicResult.message);
          setTimeout(() => {
            onClose('quote_saved_printlogic_failed');
          }, 3000);
        }
      } else {
        alert('Error saving quote: ' + result.error);
        setIsSubmittingToPrintlogic(false);
      }
    } catch (error) {
      alert('Error processing order: ' + error.message);
      setIsSubmittingToPrintlogic(false);
    }

    // Don't reset isSubmittingToPrintlogic on success - let the modal close
  };

  const handleApproveAndSubmitToPrintSystem = async () => {
    if (!quoteName.trim() || !clientName.trim()) {
      alert('Please enter both quote name and client name');
      return;
    }

    setShowPrintlogicStatusModal(true);
  };

  // Handle submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (currentQuote.items.length === 0) {
      alert('No products in the quote to submit. Please add products to the quote first.');
      return;
    }

    if (!quoteName.trim() || !clientName.trim()) {
      alert('Please enter both quote name and client name');
      return;
    }

    const triggeredByEnter = enterSubmitRef.current;
    enterSubmitRef.current = false;
    if (triggeredByEnter) {
      const confirmed = window.confirm('You pressed Enter. Submit this quote now?');
      if (!confirmed) {
        return;
      }
    }

    setIsSubmitting(true);
    const itemsToSubmit = [...currentQuote.items];

    // Create the delivery info object
    const deliveryInfo = {
      option: deliveryOption,
      cost: deliveryCost,
      boxesNeeded: deliveryOption === 'express' ? expressDeliveryDetails.boxesNeeded : 0,
      estimatedWeight: deliveryOption === 'express' ? expressDeliveryDetails.estimatedWeight : 0,
      hasOversizedItems: hasOversizedItems,
      address: deliveryOption === 'express' ? deliveryAddressText : null,
      numberOfAddresses: deliveryOption === 'multi-address' ? numberOfAddresses : null,
      addresses: deliveryOption === 'multi-address' ? manualAddresses : null,
      expressSubOption: deliveryOption === 'express' ? expressSubOption : null,
      expressSubOptionCost: deliveryOption === 'express' ? expressDeliveryDetails.expressSubOptionCost : 0,
    };

    // Create the quote object
    const quoteData = {
      quoteName,
      clientName,
      companyName,
      customerName,
      dateRequired,
      notes,
      items: itemsToSubmit,
      totalProductsPrice,
      deliveryInfo,
      totalPrice: finalTotalPrice,
      // Preserve convertedFromDraft flag if it exists on currentQuote
      ...(currentQuote.convertedFromDraft && { convertedFromDraft: true })
    };

    // Manager-specific: Add Printlogic customer data if selected
    if (selectedPrintlogicCustomer) {
      quoteData.printlogic_customer_id = selectedPrintlogicCustomer.customer_id;
      quoteData.printlogic_account_name = selectedPrintlogicCustomer.customer_name;
    }

    // Submit the quote
    const result = await addQuote(quoteData);

    if (result.success) {
      setIsSubmissionSuccessful(true);
      setSuccessMessage('Quote saved successfully!');
      setTimeout(() => {
        onClose('quote_saved');
      }, 2000);
    } else {
      alert('Error submitting quote: ' + result.error);
      setIsSubmitting(false);
    }

    // Don't reset isSubmitting on success - let the modal close
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] flex flex-col relative">
        {/* Success Message Banner - Absolutely centered overlay with grow animation */}
        {isSubmissionSuccessful && successMessage && (
          <div className="absolute inset-0 flex items-center justify-center z-50 bg-white bg-opacity-95 rounded-lg animate-fadeIn">
            <div className="bg-green-50 border-4 border-green-600 text-green-900 rounded-lg p-6 shadow-2xl max-w-2xl animate-growIn">
              <div className="flex items-center">
                <svg className="w-10 h-10 mr-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-bold text-xl mb-1">{successMessage}</p>
                  <p className="text-sm text-green-800">The quote has been saved to the system.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          @keyframes growIn {
            0% {
              transform: scale(0);
              opacity: 0;
            }
            50% {
              transform: scale(1.05);
            }
            100% {
              transform: scale(1);
              opacity: 1;
            }
          }

          @keyframes fadeIn {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }

          .animate-growIn {
            animation: growIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
          }

          .animate-fadeIn {
            animation: fadeIn 0.3s ease-in;
          }
        `}</style>

        {/* Scrollable content area */}
        <div className="p-6 overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Manager Quote Review</h2>
            <button
              onClick={() => onClose('add_more_items')}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50"
              aria-label="Add More Items"
            >
              Add More Items
            </button>
          </div>

        {!displayForm ? (
          <div className="text-center py-10">
            <p className="text-gray-500 mb-4">No products in your quote.</p>
            <button
              onClick={() => onClose(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded"
            >
              Add Some Products
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.defaultPrevented) {
                const target = event.target;
                const tag = target?.tagName;
                const type = target?.type;
                const isTextarea = tag === 'TEXTAREA';
                const isButton = tag === 'BUTTON';
                const isSubmitInput = tag === 'INPUT' && (type === 'submit' || type === 'button');
                if (!isTextarea && !isButton && !isSubmitInput) {
                  enterSubmitRef.current = true;
                }
              }
            }}
          >
            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4">Products</h3>
              
              {/* List of products already in the quote */}
              {currentQuote.items.length > 0 && (
                <div className="mb-4">
                  <div className="bg-gray-50 rounded-t border-t border-l border-r border-gray-200 p-3">
                    <div className="grid grid-cols-12 gap-2 font-medium text-sm">
                      <div className="col-span-6">Product</div>
                      <div className="col-span-2 text-center">Dimensions</div>
                      <div className="col-span-1 text-center">Qty</div>
                      <div className="col-span-2 text-right">Price</div>
                      <div className="col-span-1"></div>
                    </div>
                  </div>
                  
                  <div className="border-l border-r border-gray-200">
                    {currentQuote.items.map((product, index) => {
                      const isGroupedPiece = Boolean(product.multiPieceGroupId);
                      const groupInfo = isGroupedPiece
                        ? multiPieceGroupMeta.get(product.multiPieceGroupId)
                        : null;
                      const showGroupHeader =
                        Boolean(groupInfo) && groupInfo.firstIndex === index;
                      const isLegacyMultiPiece =
                        !isGroupedPiece && Array.isArray(product.pieces) && product.pieces.length > 0;
                      const materialSummary =
                        (Array.isArray(product.materials) && product.materials.length > 0
                          ? product.materials
                              .map((material) => material.name || material.id || '')
                              .filter(Boolean)
                              .join(', ')
                          : undefined) ||
                        product.materialNames ||
                        product.material ||
                        'N/A';
                      const finishSummary =
                        Array.isArray(product.processes) && product.processes.length > 0
                          ? product.processes.join(', ')
                          : typeof product.processes === 'string'
                          ? product.processes
                          : '';
                      const multiPieceAdjustment =
                        typeof product.multiPieceAdjustment === 'number'
                          ? product.multiPieceAdjustment
                          : 0;
                      const effectivePrice = getEffectivePrice(product);
                      const calculatedBase =
                        product.calculatedPrice ??
                        product.priceBeforeOverride ??
                        product.price ??
                        0;
                      const hasOverride = product.overridePrice !== undefined && product.overridePrice !== null;
                      const overrideDelta = hasOverride
                        ? product.overridePrice - calculatedBase
                        : 0;

                      return (
                        <React.Fragment key={product.id}>
                          {showGroupHeader && (
                            <div className="bg-blue-50 px-3 py-2 border-b border-gray-200">
                              <div className="flex flex-col gap-1 text-sm text-blue-900 sm:flex-row sm:items-center sm:justify-between">
                                <span className="font-medium">
                                  {groupInfo?.name || 'Multi-piece Group'} • {groupInfo?.count || 1}{' '}
                                  item{(groupInfo?.count || 1) !== 1 ? 's' : ''}
                                </span>
                                {groupInfo && (
                                  <span className="text-blue-800 font-medium">
                                    Group total {formatPrice(groupInfo.effectiveTotal ?? groupInfo?.summary?.finalTotal ?? 0)}
                                  </span>
                                )}
                              </div>
                            {groupInfo &&
                              Math.abs(groupInfo?.summary?.adjustment ?? 0) > 0.005 && (
                                <p className="text-xs text-blue-800 mt-1">
                                  Includes minimum charge adjustment of £
                                  {(groupInfo.summary?.adjustment ?? 0).toFixed(2)} shared across this group.
                                </p>
                              )}
                            </div>
                          )}

                          <div
                            className={`grid grid-cols-12 gap-2 p-3 border-b border-gray-200 ${
                              index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                            }`}
                          >
                            <div className="col-span-6">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{product.jobName}</p>
                                <button
                                  type="button"
                                  onClick={(e) => handleRenameStart(e, product)}
                                  className="text-blue-600 hover:text-blue-800 text-xs opacity-70 hover:opacity-100"
                                  title="Rename"
                                >
                                  ✏️
                                </button>
                              </div>

                              {renamingItemId === product.id && (
                                <div className="flex items-center gap-2 mt-2">
                                  <input
                                    type="text"
                                    value={renameValue}
                                    onChange={(e) => setRenameValue(e.target.value)}
                                    onKeyDown={handleRenameKeyPress}
                                    onBlur={handleRenameSave}
                                    className="font-medium bg-white border border-blue-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1"
                                    placeholder="Enter new name..."
                                    autoFocus
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRenameSave}
                                    className="text-green-600 hover:text-green-800 text-xs px-1"
                                    title="Save"
                                  >
                                    ✓
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleRenameCancel}
                                    className="text-red-600 hover:text-red-800 text-xs px-1"
                                    title="Cancel"
                                  >
                                    ✕
                                  </button>
                                </div>
                              )}

                              {isGroupedPiece && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Item {product.multiPieceGroupIndex} of {product.multiPieceGroupCount}
                                </p>
                              )}

                              {isLegacyMultiPiece ? (
                                <>
                                  <p className="text-sm text-gray-500">
                                    Pieces: {product.pieces.length}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Materials: {materialSummary}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="text-sm text-gray-500">{materialSummary}</p>
                                  {finishSummary && (
                                    <p className="text-sm text-gray-500">
                                      Finishing: {finishSummary}
                                    </p>
                                  )}
                                </>
                              )}

                              {product.productType === 'small_format' &&
                                product.isBooklet &&
                                product.separateCover &&
                                product.coverProcesses &&
                                product.coverProcesses.length > 0 && (
                                  <p className="text-sm text-gray-500">
                                    Cover Finishing: {product.coverProcesses.join(', ')}
                                  </p>
                                )}

                              {isGroupedPiece && Math.abs(multiPieceAdjustment) > 0.005 && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Group allocation: £{multiPieceAdjustment.toFixed(2)}
                                </p>
                              )}
                            </div>
                            <div className="col-span-2 text-center text-sm">
                              {isGroupedPiece
                                ? product.dimensions || `${product.quantity} unit${product.quantity === 1 ? '' : 's'}`
                                : isLegacyMultiPiece
                                ? `${product.pieces.length} pcs`
                                : product.dimensions}
                            </div>
                            <div className="col-span-1 text-center text-sm">
                              {product.quantity}
                            </div>
                            <div className="col-span-2 text-right text-sm space-y-1">
                              <div className="font-medium">{formatPrice(effectivePrice)}</div>
                              {hasOverride && (
                                <div className="text-xs text-gray-600">
                                  Calculated {formatPrice(calculatedBase)}
                                </div>
                              )}
                              {hasOverride && (
                                <div className="text-xs text-amber-700">
                                  Override {formatPrice(product.overridePrice)} (Δ {formatPrice(overrideDelta)})
                                </div>
                              )}
                              {canOverridePrices && (
                                <div className="space-y-1">
                                  {overrideEditId === product.id ? (
                                    <div className="space-y-1">
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={overrideValue}
                                        onChange={(e) => setOverrideValue(e.target.value)}
                                        onKeyDown={(e) => handleOverrideKeyDown(e, product)}
                                        className="w-full border border-blue-300 rounded px-2 py-1 text-right text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Override price"
                                        autoFocus
                                      />
                                      <input
                                        type="text"
                                        value={overrideReason}
                                        onChange={(e) => setOverrideReason(e.target.value)}
                                        onKeyDown={(e) => handleOverrideKeyDown(e, product)}
                                        className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Reason (optional)"
                                      />
                                      <div className="flex justify-end gap-2">
                                        <button
                                          type="button"
                                          onClick={() => saveOverride(product)}
                                          className="text-green-700 hover:text-green-900 text-xs font-medium"
                                        >
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          onClick={cancelOverrideEdit}
                                          className="text-gray-600 hover:text-gray-800 text-xs"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col items-end gap-1">
                                      <button
                                        type="button"
                                        onClick={() => startOverrideEdit(product)}
                                        className="text-blue-600 hover:text-blue-800 text-xs"
                                      >
                                        Override
                                      </button>
                                      {hasOverride && (
                                        <button
                                          type="button"
                                          onClick={() => clearOverride(product)}
                                          className="text-red-600 hover:text-red-800 text-xs"
                                        >
                                          Clear override
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="col-span-1 text-right">
                              <button
                                type="button"
                                onClick={() => removeProductFromQuote(product.id)}
                                className="text-red-600 hover:text-red-800"
                                aria-label="Remove product"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Products subtotal */}
              <div className="flex justify-between items-center border-t border-gray-200 py-3 mb-4">
                <span className="font-medium">Products Subtotal:</span>
                <span className="font-medium">£{totalProductsPrice.toFixed(2)}</span>
              </div>
            </div>

            {/* Manager-specific: Printlogic Customer Selection */}
            <div className="mb-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Printlogic Customer Assignment
              </h3>
              
              <div className="bg-blue-50 border border-blue-200 rounded p-4">
                {selectedPrintlogicCustomer ? (
                  <div>
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-medium text-blue-900">
                          Selected Customer: {selectedPrintlogicCustomer.customer_name}
                        </div>
                        <div className="text-sm text-blue-700 mt-1">
                          Customer ID: {selectedPrintlogicCustomer.customer_id}
                        </div>
                        <div className="text-xs text-blue-600 mt-2">
                          This customer will be used for Printlogic submission and will override the client name.
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={clearPrintlogicCustomer}
                        className="text-blue-700 hover:text-blue-900 text-sm"
                      >
                        Change
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3">
                      <div className="font-medium text-blue-900 mb-1">
                        No Printlogic Customer Selected
                      </div>
                      <div className="text-sm text-blue-700">
                        Select a customer to enable Printlogic submission for external customers.
                        If no customer is selected, the quote will be handled normally.
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowPrintlogicCustomerModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
                      >
                        Select Printlogic Customer
                      </button>
                      <button
                        type="button"
                        onClick={handleQuickSelectTillSale}
                        className="px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded text-sm font-medium flex items-center gap-1.5"
                        title="Quick select TILL SALE for walk-in customers"
                      >
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                        </svg>
                        TILL SALE
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Delivery Options */}
            <div className="mb-6 border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium mb-4">Delivery Options</h3>
              
              <div className="space-y-3">
                <div className="flex items-start p-3 border rounded hover:bg-gray-50">
                  <input 
                    type="radio" 
                    id="collection" 
                    name="deliveryOption"
                    checked={deliveryOption === 'collection'} 
                    onChange={() => setDeliveryOption('collection')}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <label htmlFor="collection" className="font-medium block">Collection (Free)</label>
                    <p className="text-sm text-gray-600">Collect from our premises during business hours</p>
                  </div>
                </div>
                
                <div className="border rounded hover:bg-gray-50">
                  <div className="flex items-start p-3">
                    <input
                      type="radio"
                      id="express"
                      name="deliveryOption"
                      checked={deliveryOption === 'express'}
                      onChange={() => {
                        setDeliveryOption('express');
                        if (deliveryOption !== 'express') {
                          setExpressSubOption(null);
                        }
                      }}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <label htmlFor="express" className="font-medium block">Express Delivery (£{expressDeliveryCost.toFixed(2)})</label>
                      <p className="text-sm text-gray-600">Overnight</p>
                      {expressDeliveryDetails.boxesNeeded > 1 && (
                        <p className="text-xs text-gray-500 mt-1">
                          Estimated weight: {expressDeliveryDetails.estimatedWeight}kg
                          ({expressDeliveryDetails.boxesNeeded} boxes needed at £9.50 per box)
                        </p>
                      )}
                      {expressDeliveryDetails.expressSubOptionCost > 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          Includes £{expressDeliveryDetails.expressSubOptionCost.toFixed(2)} surcharge for delivery option
                        </p>
                      )}
                      {hasOversizedItems && (
                        <p className="text-xs text-amber-600 mt-1">
                          Includes £20 surcharge for oversized items ({'>'}1m)
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Express sub-options */}
                  {deliveryOption === 'express' && (
                    <div className="pl-8 pr-3 pb-3 border-t border-gray-100">
                      <div className="space-y-2">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-standard"
                            name="expressSubOption"
                            checked={expressSubOption === null}
                            onChange={() => setExpressSubOption(null)}
                            className="mr-2"
                          />
                          <label htmlFor="express-standard" className="text-sm">Standard next day (no extra charge)</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-pre12"
                            name="expressSubOption"
                            checked={expressSubOption === 'pre12'}
                            onChange={() => setExpressSubOption('pre12')}
                            className="mr-2"
                          />
                          <label htmlFor="express-pre12" className="text-sm">Pre 12:00 (+£{(expressDeliveryDetails.boxesNeeded * 6).toFixed(2)})</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-pre1030"
                            name="expressSubOption"
                            checked={expressSubOption === 'pre1030'}
                            onChange={() => setExpressSubOption('pre1030')}
                            className="mr-2"
                          />
                          <label htmlFor="express-pre1030" className="text-sm">Pre 10:30 (+£{(expressDeliveryDetails.boxesNeeded * 10).toFixed(2)})</label>
                        </div>
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="express-saturday1030"
                            name="expressSubOption"
                            checked={expressSubOption === 'saturday1030'}
                            onChange={() => setExpressSubOption('saturday1030')}
                            className="mr-2"
                          />
                          <label htmlFor="express-saturday1030" className="text-sm">Saturday pre 10:30 (+£{(expressDeliveryDetails.boxesNeeded * 18).toFixed(2)})</label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex items-start p-3 border rounded hover:bg-gray-50">
                  <input 
                    type="radio" 
                    id="multi-address" 
                    name="deliveryOption"
                    checked={deliveryOption === 'multi-address'} 
                    onChange={() => setDeliveryOption('multi-address')}
                    className="mt-1 mr-3"
                  />
                  <div className="flex-1">
                    <label htmlFor="multi-address" className="font-medium block">Multi-Address Delivery</label>
                    <p className="text-sm text-gray-600">Deliver to multiple addresses (£9.50 per address)</p>
                    {deliveryOption === 'multi-address' && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <label htmlFor="numberOfAddresses" className="text-sm font-medium">Number of addresses:</label>
                          <input
                            type="number"
                            id="numberOfAddresses"
                            value={numberOfAddresses}
                            onChange={(e) => setNumberOfAddresses(Math.max(2, parseInt(e.target.value) || 2))}
                            min="2"
                            className="w-16 p-1 border rounded text-center"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Total cost: {numberOfAddresses} addresses × £9.50 = £{(numberOfAddresses * 9.5).toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Delivery address section */}
              {deliveryOption === 'express' && (
                <div className="mt-4 p-4 bg-gray-50 rounded border">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="font-medium">Delivery Address</h4>
                    <button
                      type="button"
                      onClick={() => setShowAddressBookModal(true)}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Select from Address Book
                    </button>
                  </div>
                  
                  {/* Show selected customer info if address was selected from address book */}
                  {selectedAddress && (
                    <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded">
                      <div className="text-sm">
                        <span className="font-medium">Selected Customer:</span> {selectedAddress.customer_name}
                        {selectedAddress.company_name && (
                          <span className="ml-2 text-gray-600">({selectedAddress.company_name})</span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedAddress(null);
                            setDeliveryAddressText('');
                          }}
                          className="ml-2 text-xs text-red-600 hover:text-red-800"
                        >
                          Change
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium mb-1">Delivery Address *</label>
                    <textarea
                      value={deliveryAddressText}
                      onChange={(e) => setDeliveryAddressText(e.target.value)}
                      className="w-full p-3 border rounded font-mono"
                      rows="10"
                      placeholder={`Enter the full delivery address - one line per field:

123 Main Street
Suite 4B
London
SW1A 1AA
Leave with reception if no answer`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Enter the complete delivery address with each component on a separate line. 
                      Include all relevant details such as street address, unit/suite, city, postcode, and any delivery instructions.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Multi-address delivery section */}
              {deliveryOption === 'multi-address' && (
                <div className="mt-4 p-4 bg-gray-50 rounded border">
                  <h4 className="font-medium mb-3">Multiple Delivery Addresses</h4>
                  
                  {numberOfAddresses <= 4 ? (
                    <>
                      <p className="text-sm text-gray-600 mb-3">
                        Please enter each delivery address on a separate line.
                      </p>
                      <div>
                        <label className="block text-sm font-medium mb-1">Delivery Addresses *</label>
                        <textarea
                          value={manualAddresses}
                          onChange={(e) => setManualAddresses(e.target.value)}
                          className="w-full p-3 border rounded"
                          rows="8"
                          placeholder="Enter each delivery address on a separate line:&#10;&#10;Jack Wilson, 123 Main St, City, Postcode&#10;Ben Smith, 456 Oak Ave, Town, Postcode&#10;Tom Jones, 789 Pine Rd, Village, Postcode"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Enter {numberOfAddresses} addresses (one per line). Include full address details for each location.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 mr-3">
                          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <h5 className="font-medium text-blue-900 mb-2">Document Upload Required</h5>
                          <p className="text-sm text-blue-800 mb-2">
                            For orders with more than 4 addresses, please upload a document containing all delivery addresses when you upload your artwork files.
                          </p>
                          <p className="text-sm text-blue-800">
                            <strong>Accepted formats:</strong> .txt, .docx, or .xlsx files<br />
                            <strong>Format:</strong> Include recipient name and full address for each location (one per line or row)
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Quote information */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium mb-1">Quote Name *</label>
                <input
                  type="text"
                  value={quoteName}
                  onChange={(e) => setQuoteName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder={
                    isTillSaleSelected
                      ? "e.g. John Smith or ABC Company"
                      : "e.g. Event Materials Package"
                  }
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  A descriptive name for this group of products
                </p>
                {isTillSaleSelected && (
                  <p className="text-xs text-amber-600 mt-1 font-medium">
                    For TILL SALE customers, enter the actual customer name to identify the order
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">
                  Client Name *
                  {selectedPrintlogicCustomer && (
                    <span className="text-xs text-blue-600 ml-1">(auto-filled, editable)</span>
                  )}
                </label>
                <input
                  type="text"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter client name"
                  required
                />
                {selectedPrintlogicCustomer && (
                  <p className="text-xs text-blue-600 mt-1">
                    Auto-filled from selected Printlogic customer. You can modify this (e.g., add the individual's name).
                  </p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Company Name (Optional)</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Enter company name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Date Required (Optional)</label>
                <input
                  type="date"
                  value={dateRequired}
                  onChange={(e) => setDateRequired(e.target.value)}
                  className="w-full p-2 border rounded"
                  min={new Date().toISOString().split('T')[0]} 
                />
              </div>
            </div>
            
            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Additional Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
                placeholder="Any additional information for this quote"
              ></textarea>
            </div>
            
            {/* Total price with delivery */}
            <div className="flex justify-between items-center border-t border-b border-gray-200 py-3 mb-6">
              <div>
                <span className="font-bold text-lg">Total:</span>
                {deliveryCost > 0 && (
                  <div className="text-sm text-gray-600">
                    Products: £{totalProductsPrice.toFixed(2)}<br />
                    Delivery: £{deliveryCost.toFixed(2)}
                  </div>
                )}
              </div>
              <span className="font-bold text-lg">£{finalTotalPrice.toFixed(2)}</span>
            </div>
            
            {/* Actions - Hide when submission is successful */}
            {!isSubmissionSuccessful && (
              <div className="flex justify-between">
                <div>
                  <button
                    type="button"
                    onClick={() => {
                      clearCurrentQuote();
                      onClose('clear_all');
                    }}
                    className="px-4 py-2 text-red-600 border border-red-600 rounded mr-2"
                  >
                    Clear All
                  </button>
                  <button
                    type="button"
                    onClick={() => onClose('add_more_items')}
                    className="px-4 py-2 border rounded"
                  >
                    Add More Items
                  </button>
                </div>

                {selectedPrintlogicCustomer ? (
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-green-400"
                      disabled={isSubmitting || currentQuote.items.length === 0}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Quote'}
                    </button>
                    <button
                      type="button"
                      onClick={handleApproveAndSubmitToPrintSystem}
                      className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-purple-400"
                      disabled={isSubmittingToPrintlogic || currentQuote.items.length === 0}
                    >
                      {isSubmittingToPrintlogic ? 'Processing...' : 'Create Order and Send to Printlogic'}
                    </button>
                  </div>
                ) : (
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded disabled:bg-green-400"
                    disabled={isSubmitting || currentQuote.items.length === 0}
                  >
                    {isSubmitting ? 'Saving...' : 'Save Quote'}
                  </button>
                )}
              </div>
            )}
          </form>
        )}
        </div> {/* End scrollable content area */}

        {/* Address Book Modal */}
        <AddressBookModal
          isOpen={showAddressBookModal}
          onClose={() => setShowAddressBookModal(false)}
          onSelectAddress={handleAddressSelected}
          onAddNewAddress={handleAddNewAddress}
        />

        {/* Manager-specific: Printlogic Customer Modal */}
        <PrintlogicCustomerModal
          isOpen={showPrintlogicCustomerModal}
          onClose={() => setShowPrintlogicCustomerModal(false)}
          onSelectCustomer={handlePrintlogicCustomerSelected}
        />

        <PrintlogicOrderStatusModal
          isOpen={showPrintlogicStatusModal}
          onCancel={() => setShowPrintlogicStatusModal(false)}
          onConfirm={(orderStatus) => {
            setShowPrintlogicStatusModal(false);
            submitOrderToPrintlogic(orderStatus);
          }}
          isSubmitting={isSubmittingToPrintlogic}
        />
      </div>
    </div>
  );
};

export default ManagerQuoteReview;
